<template>
  <v-dialog
    v-model="isOpenModal"
    scrollable
    :transition="$vuetify.breakpoint.mobile ? 'dialog-bottom-transition' : ''"
    :fullscreen="estMobile"
    width="auto"
    class="dialog"
    @click:outside="confirm"
  >
    <v-card
      class="fenetre"
      :class="estMobile ? 'fenetre-mobile' : ''"
    >
      <div class="d-flex bg-titre">
        <div
          class="d-flex pl-6"
          :class="$vuetify.breakpoint.mobile ? 'flex-column' : 'align-center'"
        >
          <div
            class="titre-h3 secondary--text align-self-center mb-2"
          >
            Mon prix d'équilibre
          </div>
          <BaseSelect
            :items="unites"
            :libelle-btn="uniteSelected.libelle"
            class="align-self-center"
            @onClick="onSelectUnite($event)"
          />
        </div>
      
        <v-btn
          text
          class="btn-close"
          @click="confirm()"
        >
          <v-icon color="secondary">
            mdi-close
          </v-icon>
        </v-btn>
      </div>
      <div
        class="content"
        :class="estMobile ? 'content-mobile' : ''"
      >
        <BaseLoader v-if="loadingRestitution" />
        <BaseMessageServerError
          v-else-if="erreurRestitution"
          message="Aucune donnée de clôture disponible pour cette culture."
        />

        <template v-else>
          <DetailPrixEquilibreResumeModal :unite="unite" />
          <div class="bloc-onglets">
            <DetailPrixEquilibreChargesModal
              :onglet-actif="ongletActif"
              :onglets-masques="ongletsMasques"
              @change="ongletActif = $event"
            />
            <ContenuRestitution
              :onglet-actif="ongletActif"
              :restitution="restitution"
              :evolution="evolution"
              :groupe="groupeRestitution"
              :unite="unite"
              :has-evolution="hasEvolution"
            />
          </div>
          <div
            v-if="hasEvolution"
            class="texte texte-gris-fonce text-right mt-2 note"
          >
            * Évolution par rapport à l'année précédente
          </div>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseSelect from '@/components/common/base-select.vue'
import BaseLoader from '@/components/common/base-loader.vue'
import BaseMessageServerError from '@/components/common/base-message-server-error.vue'
import DetailPrixEquilibreChargesModal from './detail-prix-equilibre-charges-modal.vue'
import DetailPrixEquilibreResumeModal from './detail-prix-equilibre-resume-modal.vue'
import ContenuRestitution from './restitution/contenu-restitution.vue'
import { UNITE_TONNE, UNITE_HECTARE } from '@/utils/restitution'

const ONGLET_COPRODUITS = 'Co-produits'
const ONGLET_PAR_DEFAUT = 'Charges variables'

export default {
  name: 'DetailPrixEquilibreModal',
  components: {
    BaseSelect, BaseLoader, BaseMessageServerError,
    DetailPrixEquilibreChargesModal, DetailPrixEquilibreResumeModal, ContenuRestitution
  },
  props: {
    isOpenModal: {
      type: Boolean,
      default: false
    },
    codeTypeCulture: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      unites: [
        { code: 1, libelle: '€/ha' },
        { code: 2, libelle: '€/t' }
      ],
      uniteSelected: { code: 2, libelle: '€/t' },
      ongletActif: ONGLET_PAR_DEFAUT
    }
  },
  computed: {
    ...mapGetters({
      restitution: 'cloture/restitution',
      evolution: 'cloture/evolution',
      groupeRestitution: 'cloture/groupeRestitution',
      loadingRestitution: 'cloture/loadingRestitution',
      erreurRestitution: 'cloture/erreurRestitution',
      hasEvolution: 'cloture/hasEvolution',
      aDesCoproduits: 'cloture/aDesCoproduits'
    }),
    ongletsMasques () {
      return this.aDesCoproduits ? [] : [ONGLET_COPRODUITS]
    },
    unite () {
      return this.uniteSelected.code === 1 ? UNITE_HECTARE : UNITE_TONNE
    },
    estMobile () {
      return this.$vuetify.breakpoint.mobile
    }
  },

  watch: {
    aDesCoproduits (aDesCoproduits) {
      if (!aDesCoproduits && this.ongletActif === ONGLET_COPRODUITS) {
        this.ongletActif = ONGLET_PAR_DEFAUT
      }
    }
  },

  created () {
    this.chargerRestitution()
  },

  beforeDestroy () {
    this.resetRestitution()
  },

  methods: {
    ...mapActions({
      getRestitution: 'cloture/getRestitution',
      getGroupeRestitution: 'cloture/getGroupeRestitution',
      resetRestitution: 'cloture/resetRestitution'
    }),
    chargerRestitution () {
      if (this.codeTypeCulture == null) {
        return
      }
      this.getGroupeRestitution(this.codeTypeCulture)
      return this.getRestitution(this.codeTypeCulture).catch(() => {})
    },
    confirm () {
      this.$emit('close')
    },
    onSelectUnite (code) {
      const unite = this.unites.find((u) => u.code === code)
      if (unite != null) {
        this.uniteSelected = unite
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bloc-onglets {
  display: flex;
  flex-direction: column;
}

.dialog {
  height: 85% !important;
}.fenetre {
  background-color: #f1f2f5;
  width: 912px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}
.header {
  width: 100%;
  box-sizing: border-box;
  height: 80px;
  flex: 0 0 auto;
  padding-left: 24px;
  display: flex;
  align-items: center;
}
.header-left {
  width: 800px;
  height: 40px;
  gap: 16px;
  display: flex;
  align-items: center;
}
.header-right {
  width: 80px;
  height: 80px;
  padding: 24px;
  gap: 8px;
  align-items: center;
}
.content {
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.note {
  font-size: 0.75rem;
}
.btn-close {
  position: absolute;
  right: 0;
}

@media (max-width: 960px) {
  .fenetre-mobile {
    width: 100%;
    height: 100%;
    min-height: 100%;
    overflow-y: auto;
  }
  .header-mobile {
    width: 100%;
    height: auto;
    padding: 12px 8px 12px 16px;
    align-items: center;

    .header-left {
      margin-right: auto;
    }

    .header-right {
      width: auto;
      height: auto;
      padding: 0;
    }
  }

  .header-left-mobile {
    width: auto;
    height: auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .content-mobile {
    width: 100%;
    height: auto;
    max-height: none;
    flex: 0 0 auto;
    padding: 0 8px 88px 8px;
    overflow-x: hidden;
    overflow-y: visible;
  }
}
.help-card-mobile {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  overflow-y: auto;
}

.card.help-card-mobile {
  max-height: calc(100% - 56px);
}
.bg-titre {
  height: 70px;
}

@media (max-width: 960px) {
  .bg-titre {
    height: auto;
    padding: 12px 0;
  }
}
.card {
  border-radius: 0px !important;
  background-color: #f1f2f5;
}
.btn-close {
  position: absolute;
  right: 0;
}
</style>
